import { useMutation } from '@apollo/client';
import type {
  UpdateEnterpriseRiskMutation,
  UpdateEnterpriseRiskMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateEnterpriseRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateEnterpriseRiskTRPC } from './useUpdateEnterpriseRiskTRPC';

/**
 * Flag-gated wrapper (RSP-6498). Delegates to the tRPC twin when the `trpc`
 * flag is on, else the legacy Apollo Hasura-action path. Consumers import THIS,
 * never the *TRPC twin.
 */
export const useUpdateEnterpriseRisk = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpcMutation = useUpdateEnterpriseRiskTRPC();

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'enterprise_risk');
      },
      refetchQueries: [
        namedOperations.Query.getEnterpriseRiskById,
        namedOperations.Query.getEnterpriseRisks,
      ],
    }
  );

  const updateEnterpriseRisk = async (
    variables: UpdateEnterpriseRiskMutationVariables
  ): Promise<UpdateEnterpriseRiskMutation | undefined> => {
    if (trpcEnabled) {
      return trpcMutation.updateEnterpriseRisk(variables);
    }

    const result = await updateGraphQL({ variables });

    return result.data ?? undefined;
  };

  if (trpcEnabled) {
    return {
      updateEnterpriseRisk,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateEnterpriseRisk,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
